import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Archive, Trash2, ChevronDown, Filter } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_CONFIG = {
  belum_dibuat:     { label: "Belum Dibuat",      color: "text-[#94A3B8] bg-white/5 border-white/15" },
  sedang_dikerjakan:{ label: "Sedang Dikerjakan",  color: "text-yellow-400 bg-yellow-500/10 border-yellow-500/20" },
  sudah_publish:    { label: "Sudah Publish",      color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" },
};

const STATUSES = Object.keys(STATUS_CONFIG);
const FILTERS = ["semua", ...STATUSES];

export default function ContentVault() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("semua");

  useEffect(() => {
    axios.get(`${API}/vault`)
      .then(r => setIdeas(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/vault/${id}`, { status });
    setIdeas(prev => prev.map(i => i.id === id ? { ...i, status } : i));
  };

  const deleteIdea = async (id) => {
    await axios.delete(`${API}/vault/${id}`);
    setIdeas(prev => prev.filter(i => i.id !== id));
  };

  const filtered = filter === "semua" ? ideas : ideas.filter(i => i.status === filter);
  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: ideas.filter(i => i.status === s).length }), {});

  if (loading) return <PageLoader />;

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <p className="text-xs text-indigo-400 font-mono mb-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          // ide konten tersimpan
        </p>
        <div className="flex items-end justify-between mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Content Vault
          </h1>
          <span className="text-sm text-[#94A3B8]" style={{ fontFamily: "'Inter', sans-serif" }}>
            {ideas.length} total ide
          </span>
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="grid grid-cols-3 gap-3 mb-6"
      >
        {STATUSES.map(s => (
          <div key={s} className={`border rounded-xl p-4 ${STATUS_CONFIG[s].color}`}>
            <div className="text-2xl font-bold text-white mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {counts[s] || 0}
            </div>
            <div className="text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
              {STATUS_CONFIG[s].label}
            </div>
          </div>
        ))}
      </motion.div>

      {/* Filter */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <Filter className="w-3.5 h-3.5 text-[#94A3B8]" />
        {FILTERS.map(f => (
          <button
            key={f}
            data-testid={`filter-${f}`}
            onClick={() => setFilter(f)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-200 ${
              filter === f
                ? "bg-indigo-500/20 border-indigo-500/30 text-indigo-300"
                : "border-white/10 text-[#94A3B8] hover:text-white"
            }`}
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {f === "semua" ? `Semua (${ideas.length})` : STATUS_CONFIG[f].label}
          </button>
        ))}
      </div>

      {/* Ideas List */}
      {filtered.length === 0 ? (
        <EmptyState filter={filter} />
      ) : (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="space-y-3"
        >
          <AnimatePresence>
            {filtered.map((idea, i) => (
              <motion.div
                key={idea.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ delay: i * 0.04 }}
                data-testid={`vault-item-${idea.id}`}
                className="bg-[#121216] border border-white/10 rounded-xl p-5 group"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {/* Meta */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-[10px] font-mono text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-2 py-0.5 rounded-full">
                        Kat. {idea.category}
                      </span>
                      {(idea.platforms || []).map(p => (
                        <span key={p} className="text-[10px] text-purple-400 bg-purple-500/10 border border-purple-500/20 px-2 py-0.5 rounded-full">
                          {p}
                        </span>
                      ))}
                    </div>

                    {/* Title */}
                    <h3 className="text-sm font-semibold text-white mb-1.5 leading-snug" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      {idea.title}
                    </h3>

                    {/* Angle */}
                    {idea.angle && (
                      <p className="text-xs text-indigo-300 mb-1.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                        {idea.angle}
                      </p>
                    )}

                    {/* Hook */}
                    {idea.hook && (
                      <p className="text-xs text-[#94A3B8] italic" style={{ fontFamily: "'IBM Plex Serif', serif" }}>
                        "{idea.hook}"
                      </p>
                    )}

                    {/* References */}
                    {idea.references?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {idea.references.map((ref, ri) => (
                          <span key={ri} className="text-[10px] text-[#94A3B8] bg-white/5 border border-white/10 px-2 py-0.5 rounded-full">
                            {ref}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Source Lesson */}
                    <p className="text-[10px] text-[#4a5568] mt-2" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      dari: {idea.lesson_title}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <StatusDropdown
                      current={idea.status}
                      onChange={s => updateStatus(idea.id, s)}
                    />
                    <button
                      data-testid={`delete-idea-${idea.id}`}
                      onClick={() => deleteIdea(idea.id)}
                      className="text-[#4a5568] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all duration-200 p-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );
}

function StatusDropdown({ current, onChange }) {
  const [open, setOpen] = useState(false);
  const config = STATUS_CONFIG[current] || STATUS_CONFIG.belum_dibuat;
  return (
    <div className="relative">
      <button
        data-testid="status-dropdown"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border transition-colors duration-200 ${config.color}`}
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        {config.label}
        <ChevronDown className="w-3 h-3" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.95 }}
            transition={{ duration: 0.1 }}
            className="absolute right-0 top-full mt-1 bg-[#1A1A20] border border-white/15 rounded-xl overflow-hidden z-20 min-w-[160px]"
          >
            {STATUSES.map(s => (
              <button
                key={s}
                onClick={() => { onChange(s); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors duration-150 ${
                  s === current ? "text-white bg-white/5" : "text-[#94A3B8]"
                }`}
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {STATUS_CONFIG[s].label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function EmptyState({ filter }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <Archive className="w-10 h-10 text-[#4a5568] mb-3" />
      <p className="text-[#94A3B8] text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
        {filter === "semua"
          ? "Belum ada ide konten. Selesaikan lesson pertama untuk generate ide otomatis."
          : `Tidak ada ide dengan status "${STATUS_CONFIG[filter]?.label}".`
        }
      </p>
    </div>
  );
}

function PageLoader() {
  return (
    <div className="p-8 flex items-center justify-center min-h-[60vh]">
      <div className="w-6 h-6 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
    </div>
  );
}
